import { useState } from "react"
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material"
import { alpha } from "@mui/material/styles"
import { Sparkles, ArrowRight } from "lucide-react"
import { useToast } from "@crm-src/hooks/use-toast"
import { useTypewriter } from "@crm-src/hooks/useTypewriter"
import { toSubjectLine } from "@crm-src/lib/ai-text"
import api from "@crm-src/lib/api"

interface AiTextImproverProps {
  currentText: string
  onImprove: (improvedText: string) => void
  fieldType: 'short' | 'long' // short = title/headline, long = paragraph/content
  context?: string // What this field is for
  disabled?: boolean
}

export function AiTextImprover({
  currentText,
  onImprove,
  fieldType,
  context = "",
  disabled = false
}: AiTextImproverProps) {
  const [open, setOpen] = useState(false)
  const [improvedText, setImprovedText] = useState("")
  const [isImproving, setIsImproving] = useState(false)
  const { toast } = useToast()
  const animateText = useTypewriter()

  // OpenAI requests go through the authenticated server-side proxy.

  const handleImproveClick = async () => {
    // Security check: Ensure we have text to improve
    if (!currentText || currentText.trim().length === 0) {
      toast({
        title: "⚠️ No Text to Improve",
        description: "Please enter some text first before trying to improve it.",
        variant: "destructive",
      })
      return
    }

    // Additional security: Ensure text has minimum length for meaningful improvement
    if (currentText.trim().length < 3) {
      toast({
        title: "⚠️ Text Too Short",
        description: "Please enter at least 3 characters for the AI to improve.",
        variant: "destructive",
      })
      return
    }

    setOpen(true)
    setIsImproving(true)
    setImprovedText("")

    try {
      const systemMessage = fieldType === 'short'
        ? `You are an expert copywriter for XOP Networks, a leading conferencing technology company.

Company Context:
XOP Networks designs and delivers conferencing solutions for mission-critical industries including aviation, oil & gas, power generation, and public safety.

Task: Improve the following ${context || 'headline/title'}.

Requirements:
- This is a one-line email SUBJECT — output a SINGLE line only
- NEVER write an email body, greeting, paragraph, or multiple sentences
- Keep it concise and under 60 characters
- Make it more compelling and engaging
- Maintain the core message but enhance clarity and impact
- Use active voice
- NO quotation marks
- Professional B2B tone
- DO NOT add words like "Enhanced", "Improved", "Better", "Optimized" or similar meta-commentary
- The improved text should be natural and not obviously "upgraded"
- Subtle improvements only - don't announce that you improved it

Return ONLY the improved subject line, nothing else.`
        : `You are an expert copywriter for XOP Networks, a leading conferencing technology company.

Company Context:
XOP Networks designs and delivers conferencing solutions for mission-critical industries including aviation, oil & gas, power generation, and public safety.

Task: Improve the following ${context || 'content'}.

Requirements:
- Enhance clarity, flow, and engagement
- Keep the same general length (don't make it significantly longer or shorter)
- Maintain the core message and key points
- Use professional B2B tone
- Focus on benefits and value
- Use active voice where appropriate
- Keep it relevant to the technology/conferencing industry
- DO NOT add words like "Enhanced", "Improved", "Better", "Optimized" or similar meta-commentary
- The improved text should read naturally, not like an obvious "upgrade"
- Make subtle improvements without announcing that you improved it

Return ONLY the improved version, nothing else.`

      const userPrompt = `Original text: ${currentText}\n\nImprove this text naturally. Do not add words like "Enhanced", "Improved", "Better", etc. Just make it read better while keeping the same meaning. Return only the improved text, nothing else.`
      // Use the shared CRM axios client — it re-mints the short-lived crm token
      // and retries on a 401 (a raw fetch just failed on token expiry).
      const aiResp = await api.post("/ai/generate", {
        prompt: userPrompt,
        system_prompt: systemMessage,
        model: "gpt-3.5-turbo",
        temperature: 0.7,
        // short = subject line: a tight cap (gateway floor is 64) bounds how far
        // the model can ramble; toSubjectLine() below is the hard guarantee.
        max_tokens: fieldType === 'short' ? 64 : 800,
        // SHORT (subject/title) ONLY: top_k:0 = no KB grounding. The content
        // persona was latching onto internal KB style/config chunks and rewriting
        // THOSE into the subject ("improve subject → KB dump"). LONG body content
        // keeps the gateway's default KB grounding so it can cite real product
        // facts (the documented workspace rule).
        ...(fieldType === 'short' ? { top_k: 0 } : {}),
      })
      const aiJson = aiResp.data
      // crm-api returns the gateway shape { text, ... }; reading only
      // choices/content yielded an EMPTY string (the "improve not working" bug).
      const improved = (aiJson?.text || aiJson?.choices?.[0]?.message?.content || aiJson?.content || "").trim()

      // Remove any quotation marks that might be added
      const cleanedImproved = improved.replace(/^["']|["']$/g, '')
      // A SHORT field is an email subject — hard-clamp to a single subject line
      // so a stray multi-sentence/body answer never lands in the subject box.
      const finalImproved = fieldType === 'short' ? toSubjectLine(cleanedImproved) : cleanedImproved

      setIsImproving(false)
      // Typewriter: type the improved version into the preview so it reads like
      // the AI is rewriting it live.
      animateText(finalImproved, setImprovedText)

    } catch (error) {
      console.error('Error improving text:', error)
      toast({
        title: "Error",
        description: "Failed to improve text. Please try again.",
        variant: "destructive",
      })
      setIsImproving(false)
      setOpen(false)
    }
  }

  const handleAccept = () => {
    onImprove(improvedText)
    setOpen(false)
    toast({
      title: "✨ Text Improved!",
      description: "Your improved text has been applied.",
    })
  }

  const handleCancel = () => {
    setOpen(false)
    setImprovedText("")
  }

  return (
    <>
      <Button
        type="button"
        variant="outlined"
        color="primary"
        size="small"
        onClick={handleImproveClick}
        disabled={disabled || !currentText || currentText.trim().length === 0}
        startIcon={<Sparkles size={14} />}
        sx={{ height: 32 }}
      >
        Improve
      </Button>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth={false}
        PaperProps={{ sx: { maxWidth: "95vw", width: 2000, maxHeight: "85vh" } }}
      >
        <DialogTitle sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box sx={{ color: "primary.main", display: "flex" }}><Sparkles size={20} /></Box>
            AI Text Improvement
          </Box>
          <Typography sx={{ fontSize: 14, color: "text.secondary", fontWeight: 400 }}>
            Compare the original text with the AI-improved version
          </Typography>
        </DialogTitle>

        <DialogContent>
          <Box sx={{ position: "relative", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 4, py: 2 }}>
            {/* Original Text - Left Side */}
            <Card variant="outlined">
              <CardHeader
                sx={{ pb: 1.5 }}
                title={
                  <Typography sx={{ fontSize: 14, fontWeight: 500, color: "text.secondary" }}>
                    Original Text
                  </Typography>
                }
              />
              <CardContent>
                <Box sx={{ height: 400, width: "100%", pr: 2, overflowY: "auto" }}>
                  {currentText && currentText.trim().length > 0 ? (
                    <Typography sx={{ fontSize: 14, whiteSpace: "pre-wrap", lineHeight: 1.6 }}>
                      {currentText}
                    </Typography>
                  ) : (
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
                      <Typography sx={{ fontSize: 14, color: "text.secondary", fontStyle: "italic" }}>
                        No text provided
                      </Typography>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>

            {/* Arrow/Divider */}
            <Box sx={{ position: "absolute", left: "50%", top: "50%", transform: "translate(-50%, -50%)", zIndex: 10 }}>
              <Box sx={{ bgcolor: "background.paper", border: 2, borderColor: "divider", borderRadius: "50%", p: 1, display: "flex" }}>
                <Box sx={{ color: "primary.main", display: "flex" }}><ArrowRight size={24} /></Box>
              </Box>
            </Box>

            {/* Improved Text - Right Side */}
            <Card variant="outlined" sx={{ borderColor: "primary.main", bgcolor: (t) => alpha(t.palette.primary.main, 0.06) }}>
              <CardHeader
                sx={{ pb: 1.5 }}
                title={
                  <Typography sx={{ fontSize: 14, fontWeight: 500, color: "primary.main", display: "flex", alignItems: "center", gap: 1 }}>
                    <Sparkles size={16} />
                    AI Improved Version
                  </Typography>
                }
              />
              <CardContent>
                <Box sx={{ height: 400, width: "100%", pr: 2, overflowY: "auto" }}>
                  {isImproving ? (
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
                      <Box sx={{ textAlign: "center" }}>
                        <CircularProgress size={32} sx={{ color: "primary.main", mb: 1 }} />
                        <Typography sx={{ fontSize: 14, color: "text.secondary" }}>
                          Improving your text...
                        </Typography>
                      </Box>
                    </Box>
                  ) : (
                    <Typography sx={{ fontSize: 14, whiteSpace: "pre-wrap", lineHeight: 1.6 }}>
                      {improvedText}
                    </Typography>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button
            type="button"
            variant="outlined"
            color="inherit"
            onClick={handleCancel}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="contained"
            onClick={handleAccept}
            disabled={isImproving || !improvedText}
            startIcon={<Sparkles size={16} />}
          >
            Accept Improved Version
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
